// utils/imageThumb.js
// ─────────────────────────────────────────────────────────────
// OSS 缩略图工具：把自家 OSS 原图 URL 转成带 x-oss-process 缩略参数的 URL，
// 让画布卡片加载几十 KB 的小图而非几 MB 的原图，首开快一个数量级。
//
// 安全红线：只能对「自家 OSS 桶」拼参数。第三方 aliyuncs 域名（如 MJ 厂商桶）
//   加了 x-oss-process 会直接 403 断图。自家 host 列表由后端 toolbox-config
//   下发（getOwnOSSHosts），启动时通过 setOwnOssHosts() 注入本模块。
// ─────────────────────────────────────────────────────────────

// 模块级：自家 OSS host 列表（不含协议），由 EditorPage 启动时注入
let OWN_OSS_HOSTS = [];

/**
 * 注入自家 OSS 域名列表（来自 /api/toolbox-config 的 ownOssHosts 字段）。
 * 未注入前 ossThumb 一律返回原图，保证「宁可慢、绝不断图」。
 * @param {string[]} hosts
 */
export function setOwnOssHosts(hosts) {
  if (Array.isArray(hosts)) {
    OWN_OSS_HOSTS = hosts.filter(h => typeof h === 'string' && h);
  }
}

/**
 * 判断 URL 是否指向自家 OSS 桶（可安全拼缩略参数）。
 * @param {string} url
 * @returns {boolean}
 */
export function isOwnOssUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!/^https?:\/\//i.test(url)) return false; // blob:/data:/local-cache:// 不处理
  return OWN_OSS_HOSTS.some(h => url.includes(h));
}

/**
 * 生成 OSS 缩略图 URL。仅对自家 OSS 图生效，其余（第三方图 / blob / data /
 * local-cache:// / 已带 x-oss-process 的）原样返回。
 *
 * @param {string} url   原图 URL
 * @param {number} width 目标宽度（px），默认 768
 * @param {object} [opts]
 * @param {number} [opts.quality=80] 质量 1-100
 * @returns {string} 缩略图 URL 或原 URL
 */
export function ossThumb(url, width = 768, opts = {}) {
  if (!isOwnOssUrl(url)) return url;
  // 已带处理参数的不再叠加，避免 x-oss-process 冲突
  if (url.includes('x-oss-process=')) return url;

  const quality = Math.min(100, Math.max(1, opts.quality || 80));
  // resize/w 只缩不放（limit_1 默认：图比目标小则不处理），保持宽高比
  // 输出统一走 image/resize + image/quality，interlace 让 JPEG 渐进加载
  const process = `image/resize,w_${Math.round(width)}/quality,q_${quality}/interlace,1`;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}x-oss-process=${process}`;
}

/**
 * 生成 OSS 视频「首帧截图」URL —— 让画布视频卡片像图片缩略图一样即取即显首帧，
 * 无需下载视频、不受浏览器 preload 偷懒影响，老视频新视频都适用。
 *
 * 用 OSS 服务端 video/snapshot 处理：t_0 取第 0 帧，f_jpg 输出 JPEG，w 指定宽度，
 * m_fast 取最近关键帧（快）。仅对自家 OSS 视频有效；其余（第三方/blob/data/
 * 已带 x-oss-process 的）返回空串，调用方回退到其它占位。
 *
 * @param {string} url 视频 URL（自家 OSS 的 .mp4）
 * @param {number} width 截图宽度（px），默认 768
 * @returns {string} 截帧 URL，或空串
 */
export function ossVideoPoster(url, width = 768) {
  if (!isOwnOssUrl(url)) return '';
  if (url.includes('x-oss-process=')) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}x-oss-process=video/snapshot,t_0,f_jpg,w_${Math.round(width)},h_0,m_fast`;
}

// ─────────────────────────────────────────────────────────────
// 原图真实尺寸缓存（URL → {w,h}）
//   卡片显示缩略图后，分辨率标签需要「原图绝对像素」。探测结果存这里而非组件
//   state，跨节点重挂（全屏进出、滚出滚回）不丢失 → 标签一旦出现就不再消失。
//   模块级 Map，随页面存活；不涉及跨用户敏感数据，无需在切换用户时清理。
// ─────────────────────────────────────────────────────────────
const dimensionCache = new Map(); // key: 归一化 URL → { w, h }

// 归一化 URL：剥掉 x-oss-process 缩略参数和重试用的 _r= 时间戳，
//   使「原图」「768 缩略图」「带 _r 的重试 URL」共享同一尺寸缓存键。
function normalizeDimKey(url) {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/([?&])x-oss-process=[^&]*/i, '$1')
            .replace(/([?&])_r=\d+/i, '$1')
            .replace(/[?&]+$/, '');
}

/**
 * 读取已缓存的原图尺寸。
 * @param {string} url 原图 URL（带不带缩略参数都可）
 * @returns {{w:number,h:number}|null}
 */
export function getCachedDimensions(url) {
  if (!url) return null;
  return dimensionCache.get(normalizeDimKey(url)) || null;
}

/**
 * 写入原图尺寸缓存。w/h 非法则忽略。
 * @param {string} url
 * @param {number} w
 * @param {number} h
 */
export function setCachedDimensions(url, w, h) {
  if (!url || !w || !h || w <= 0 || h <= 0) return;
  dimensionCache.set(normalizeDimKey(url), { w, h });
}

/**
 * 用 OSS `image/info` 接口拿原图「宽/高/文件大小」——只返回几百字节 JSON，
 * 不下载整张原图。iPad 上再多节点也不会被 Safari 并发限流，比 new Image()
 * 拉全图量尺寸可靠得多，还顺带拿到体积（省掉对原图的 fetch）。
 *
 * 仅对自家 OSS 图有效；第三方图/blob 返回 null，交给调用方走 new Image() 兜底。
 * @param {string} url 原图 URL
 * @param {AbortSignal} [signal]
 * @returns {Promise<{w:number,h:number,bytes:number}|null>}
 */
export async function fetchOssImageInfo(url, signal) {
  if (!isOwnOssUrl(url)) return null;
  // 剥掉已有的 x-oss-process，换成 image/info
  const base = url.replace(/([?&])x-oss-process=[^&]*/i, '$1').replace(/[?&]+$/, '');
  const sep = base.includes('?') ? '&' : '?';
  const infoUrl = `${base}${sep}x-oss-process=image/info`;
  try {
    const resp = await fetch(infoUrl, { signal });
    if (!resp.ok) return null;
    const info = await resp.json();
    // OSS 返回形如 { ImageWidth:{value:"1024"}, ImageHeight:{value:"768"}, FileSize:{value:"345678"} }
    const w = parseInt(info?.ImageWidth?.value, 10);
    const h = parseInt(info?.ImageHeight?.value, 10);
    const bytes = parseInt(info?.FileSize?.value, 10) || 0;
    if (w > 0 && h > 0) {
      setCachedDimensions(url, w, h);
      return { w, h, bytes };
    }
  } catch (_) { /* 网络/CORS/解析失败 → 交给兜底 */ }
  return null;
}

